import { useEffect, useState } from 'react';
import NProgress from 'nprogress';

/**
 * Generic content-area skeleton used as the Suspense fallback for in-app
 * screens (see `Screen` in `src/router.tsx`).
 *
 * On a hard refresh the routed screen's lazy chunk still has to download, and
 * until it mounts we cannot show the screen's OWN skeleton (its code isn't
 * loaded yet). Rather than leave the middle blank for that window, this shows a
 * neutral "stack loading" placeholder — a hero band, a row of stat tiles and a
 * table — that fills only the content region (the sidebar and header stay put),
 * then the real screen (and its own skeleton) takes over the moment it mounts.
 *
 * Delayed ~200ms so warm / prefetched chunks (which mount almost instantly)
 * never flash the skeleton, keeping navigation feeling snappy.
 */

const SkeletonStyles = () => (
  <style>{`
    @keyframes kskel-shimmer { 0%{ background-position:-450px 0 } 100%{ background-position:450px 0 } }
    .kskel{ padding:22px 25px; width:100%; min-height:calc(100vh - 120px); box-sizing:border-box; }
    .kskel-block{
      border-radius:14px;
      background:linear-gradient(90deg, rgba(21,18,58,.05) 25%, rgba(21,18,58,.10) 37%, rgba(21,18,58,.05) 63%);
      background-size:900px 100%;
      animation:kskel-shimmer 1.4s ease-in-out infinite;
    }
    .kskel-hero{ height:150px; border-radius:22px; margin-bottom:18px; }
    .kskel-tiles{ display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:20px; }
    .kskel-tile{ height:74px; }
    .kskel-row{ height:52px; margin-bottom:10px; }
    .kskel-row.head{ height:44px; margin-bottom:14px; opacity:.85; }
    @media (max-width:900px){ .kskel-tiles{ grid-template-columns:repeat(2,1fr); } }
    @media (max-width:560px){ .kskel-tiles{ grid-template-columns:1fr; } }

    /* dark mode: brighten the shimmer so it reads on the dark shell */
    html.dark .kskel-block{
      background:linear-gradient(90deg, rgba(255,255,255,.05) 25%, rgba(255,255,255,.11) 37%, rgba(255,255,255,.05) 63%);
      background-size:900px 100%;
    }
  `}</style>
);

function ScreenSkeleton() {
  // Delay so fast / prefetched chunk loads don't flash the skeleton.
  const [show, setShow] = useState(false);

  useEffect(() => {
    NProgress.start();
    const timer = setTimeout(() => setShow(true), 200);
    return () => {
      clearTimeout(timer);
      NProgress.done();
    };
  }, []);

  return (
    <div
      className="kskel"
      style={{
        opacity: show ? 1 : 0,
        transition: 'opacity .25s ease',
        pointerEvents: 'none'
      }}
      aria-hidden="true"
    >
      <SkeletonStyles />
      <div className="kskel-block kskel-hero" />
      <div className="kskel-tiles">
        <div className="kskel-block kskel-tile" />
        <div className="kskel-block kskel-tile" />
        <div className="kskel-block kskel-tile" />
        <div className="kskel-block kskel-tile" />
      </div>
      <div className="kskel-block kskel-row head" />
      <div className="kskel-block kskel-row" />
      <div className="kskel-block kskel-row" />
      <div className="kskel-block kskel-row" />
      <div className="kskel-block kskel-row" />
      <div className="kskel-block kskel-row" />
    </div>
  );
}

export default ScreenSkeleton;
